import React, { useState, useEffect } from 'react';

interface PasswordProtectionProps {
  children: React.ReactNode;
}

const PasswordProtection: React.FC<PasswordProtectionProps> = ({
  children,
}) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(true);

  // Mot de passe configuré via variable d'environnement
  const SITE_PASSWORD = import.meta.env.VITE_SITE_PASSWORD;

  useEffect(() => {
    // Si aucun mot de passe n'est configuré, désactiver la protection
    if (!SITE_PASSWORD) {
      setIsLoading(false);
      return;
    }

    // Vérifier si l'utilisateur est déjà authentifié
    const isAuth = sessionStorage.getItem('site_authenticated') === 'true';
    setIsAuthenticated(isAuth);
    setIsLoading(false);
  }, [SITE_PASSWORD]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (password === SITE_PASSWORD) {
      setIsAuthenticated(true);
      sessionStorage.setItem('site_authenticated', 'true');
    } else {
      setError('Mot de passe incorrect');
      setPassword('');
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    sessionStorage.removeItem('site_authenticated');
    setPassword('');
  };

  // Si aucun mot de passe n'est configuré, pas de protection
  if (!SITE_PASSWORD) {
    return <>{children}</>;
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="max-w-md w-full space-y-8 p-8">
          <div className="text-center">
            <h2 className="mt-6 text-3xl font-extrabold text-gray-900 dark:text-gray-100">
              Accès Protégé
            </h2>
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
              Veuillez entrer le mot de passe pour accéder au site
            </p>
          </div>

          <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
            <div>
              <label htmlFor="password" className="sr-only">
                Mot de passe
              </label>
              <input
                id="password"
                name="password"
                type="password"
                required
                className="relative block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 placeholder-gray-500 dark:placeholder-gray-400 text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-800 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
                placeholder="Mot de passe"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
              />
            </div>

            {error && (
              <div className="text-red-600 dark:text-red-400 text-sm text-center">
                {error}
              </div>
            )}

            <div>
              <button
                type="submit"
                className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200"
              >
                Accéder au site
              </button>
            </div>
          </form>

          <div className="text-center">
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Contactez l'administrateur pour obtenir le mot de passe
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="relative reset-transform">
      {/* Bouton de déconnexion en haut à droite */}
      <button
        onClick={handleLogout}
        className="fixed top-4 right-4 z-50 bg-red-600 hover:bg-red-700 text-white px-3 py-1 rounded-md text-sm transition-colors duration-200"
        title="Se déconnecter"
      >
        Déconnexion
      </button>

      {children}
    </div>
  );
};

export default PasswordProtection;
